import { useEffect } from 'react';
import { plural } from '@lingui/core/macro';

// eslint-disable-next-line import/no-extraneous-dependencies
import invariant from 'invariant';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useIdleTimer } from 'react-idle-timer';

import { doLogout } from './useLogout';

const ONE_SECOND_MS = 1_000;
const ONE_MINUTE_MS = 60 * ONE_SECOND_MS;
export const DEFAULT_SESSION_TIMEOUT_MINUTES = 30;
export const MAX_SESSION_TIMEOUT_MINUTES = 24 * 24 * 60; // 24 days

/**
 * This idle logout hook is designed to log out users after a period of inactivity.
 *
 * For now we have a default of 30 minutes, and this is configurable per org. If the
 * application has been idle for that number of minutes, we log the user out.
 *
 * If for some reason the org's timeout is > 24 days, we cap it at 24 days.
 * This is because the idle timeout in ms needs to fit in a 32-bit integer,
 * and 2^31 - 1 ms ~= 24.8 days.
 */
export default function useIdleLogout({
  sessionTimeoutMinutes,
}: {
  sessionTimeoutMinutes: number | null;
}) {
  const finalTimeoutMinutes = Math.min(
    sessionTimeoutMinutes || DEFAULT_SESSION_TIMEOUT_MINUTES,
    MAX_SESSION_TIMEOUT_MINUTES
  );
  const finalTimeoutMs = finalTimeoutMinutes * ONE_MINUTE_MS;

  invariant(finalTimeoutMinutes > 0, 'Idle session timeout must be positive');

  useEffect(() => {
    console.info(
      `Idle session timeout set to ${finalTimeoutMinutes} ${plural(
        finalTimeoutMinutes,
        {
          one: 'minute',
          other: 'minutes',
        }
      )}`
    );
  }, [finalTimeoutMinutes]);

  useIdleTimer({
    onIdle: () => {
      console.info('Logging out due to idle timeout');
      void doLogout('idle-timeout');
    },
    crossTab: true,
    leaderElection: true,
    timeout: finalTimeoutMs,
    throttle: ONE_SECOND_MS,
    syncTimers: ONE_SECOND_MS,
  });
}
